// src/components/CreateJobModal.jsx
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Briefcase,
  Building2,
  MapPin,
  Wifi,
  Clock,
  Euro,
  Link2,
  Code2,
  FileText,
  Loader2,
  AlertCircle,
  Plus,
  Lock
} from 'lucide-react';
import api from '../api/api';
import styles from './CreateJobModal.module.css';

const INITIAL_STATE = {
  title: '',
  company_name: '',
  location: '',
  remote_mode: '',
  salary_info: '',
  experience_required: '',
  skills: '',
  url: '',
  description: '',
  is_private: false, // <--- NOUVEAU
};

const CreateJobModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/jobs', formData);
      setFormData(INITIAL_STATE);
      onSuccess();
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Une erreur est survenue lors de la création.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ── Contenu du modal ──
  const modalContent = (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="createJobTitle"
    >
      <div className={styles.modal}>

        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon}>
              <Briefcase size={20} strokeWidth={1.75} />
            </div>
            <div>
              <h2 id="createJobTitle" className={styles.title}>
                Créer une offre manuelle
              </h2>
              <p className={styles.subtitle}>
                Ajoutez une mission freelance ou portage
              </p>
            </div>
          </div>

          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Fermer"
            type="button"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className={styles.body}>
          {error && (
            <div className={styles.errorBox}>
              <AlertCircle size={16} strokeWidth={2} />
              {error}
            </div>
          )}

          <form
            id="createJobForm"
            onSubmit={handleSubmit}
            className={styles.form}
          >

            <div className={styles.sectionLabel}>Informations principales</div>

            <div className={styles.row}>
              <Field icon={Briefcase} label="Titre de la mission" required>
                <input
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="ex: Développeur Full-Stack React"
                  className={styles.input}
                />
              </Field>

              <Field icon={Building2} label="Entreprise (Client)" required>
                <input
                  name="company_name"
                  required
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="ex: Zone RH"
                  className={styles.input}
                />
              </Field>
            </div>

            <div className={styles.sectionLabel}>Localisation & modalités</div>

            <div className={styles.row}>
              <Field icon={MapPin} label="Localisation">
                <input
                  name="location"
                  placeholder="ex: Paris 75"
                  value={formData.location}
                  onChange={handleChange}
                  className={styles.input}
                />
              </Field>

              <Field icon={Wifi} label="Télétravail">
                <select
                  name="remote_mode"
                  value={formData.remote_mode}
                  onChange={handleChange}
                  className={`${styles.input} ${styles.select}`}
                >
                  <option value="">Non précisé</option>
                  <option value="Oui">Oui (Total ou Hybride)</option>
                  <option value="Non">Non</option>
                </select>
              </Field>
            </div>

            <div className={styles.row}>
              <Field icon={Clock} label="Expérience requise">
                <input
                  name="experience_required"
                  placeholder="ex: 3 ans"
                  value={formData.experience_required}
                  onChange={handleChange}
                  className={styles.input}
                />
              </Field>

              <Field icon={Euro} label="Budget (TJM ou Enveloppe)">
                <input
                  name="salary_info"
                  placeholder="ex: 500€ - 600€ / jour"
                  value={formData.salary_info}
                  onChange={handleChange}
                  className={styles.input}
                />
              </Field>
            </div>

            <div className={styles.sectionLabel}>Détails complémentaires</div>

            <Field icon={Link2} label="URL de candidature (Optionnel)">
              <input
                name="url"
                placeholder="https://..."
                value={formData.url}
                onChange={handleChange}
                className={styles.input}
              />
            </Field>

            <Field icon={Code2} label="Compétences (séparées par une virgule)">
              <input
                name="skills"
                placeholder="ex: React, Node.js, SQL"
                value={formData.skills}
                onChange={handleChange}
                className={styles.input}
              />
            </Field>

            <div className={styles.row}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#555', marginBottom: '15px', fontWeight: '500' }}>
                    <input 
                    type="checkbox" 
                    name="is_private" 
                    checked={formData.is_private} 
                    onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })} 
                    style={{ width: '16px', height: '16px', accentColor: '#000' }}
                    />
                    <Lock size={15} /> Marquer cette offre comme "Privée" (Accessible uniquement aux clients autorisés)
                </label>
            </div>
            
            <Field icon={FileText} label="Description de la mission" required>
              <textarea
                name="description"
                required
                rows="6"
                value={formData.description}
                onChange={handleChange}
                placeholder="Décrivez le contexte, les missions, l'environnement technique…"
                className={`${styles.input} ${styles.textarea}`}
              />
            </Field>
          </form>
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelBtn}
            disabled={loading}
          >
            Annuler
          </button>

          <button
            type="submit"
            form="createJobForm"
            disabled={loading}
            className={styles.submitBtn}
          >
            {loading ? (
              <>
                <Loader2 size={16} strokeWidth={2.5} className={styles.spin} />
                Création...
              </>
            ) : (
              <>
                <Plus size={16} strokeWidth={2.5} />
                Créer l'offre
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Portal : monte le modal directement sur document.body ──
  // Échappe au stacking context de .main-content (qui piège les
  // éléments position:fixed sous la navbar à cause de son z-index:1)
  return createPortal(modalContent, document.body);
};

// ─────────────────────────────────────────────────────────────
// FIELD WRAPPER
// ─────────────────────────────────────────────────────────────
const Field = ({ icon: Icon, label, required, children }) => (
  <div className={styles.field}>
    <label className={styles.label}>
      <Icon size={13} strokeWidth={2} className={styles.labelIcon} />
      {label}
      {required && <span className={styles.required}>*</span>}
    </label>
    {children}
  </div>
);

export default CreateJobModal;